import { MarketplaceCartItemWithProduct } from "@redotech/redo-model/recommerce/marketplace-cart";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoDrawer } from "@redotech/redo-web/arbiter-components/modal/redo-drawer";
import { Flex } from "@redotech/redo-web/flex";
import { ImageDisplayWithPlaceholder } from "@redotech/redo-web/image-display-with-placeholder";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo } from "react";
import { useCart } from "../../context";
import * as cartDrawerStyles from "./cart-drawer.module.css";

export interface CartDrawerProps {
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onCheckout?: () => void;
}

interface CartItemRowProps {
  item: MarketplaceCartItemWithProduct;
  onUpdateQuantity: (
    productId: string,
    variantId: string,
    quantity: number,
  ) => void;
  onRemove: (productId: string, variantId: string) => void;
  isUpdating: boolean;
  error?: string;
  onClearError: () => void;
}

const CartItemRow = memo(function CartItemRow({
  item,
  onUpdateQuantity,
  onRemove,
  isUpdating,
  error,
  onClearError,
}: CartItemRowProps) {
  const handleRemove = useCallback(() => {
    onRemove(item.productId, item.variantId);
  }, [item.productId, item.variantId, onRemove]);

  const handleSetValue = useCallback(
    (newQuantity: number) => {
      onUpdateQuantity(item.productId, item.variantId, newQuantity);
    },
    [item.productId, item.variantId, onUpdateQuantity],
  );

  const featuredImage = item.product.images[0];
  const itemPrice = item.variant.price || 0;
  const totalPrice = itemPrice * item.quantity;

  const maxQuantity = useMemo(
    () => item.variant.available,
    [item.variant.available],
  );

  return (
    <Flex
      className={cartDrawerStyles.cartItem}
      dir="row"
      gap="md"
      px="3xl"
      py="lg"
    >
      {featuredImage ? (
        <ImageDisplayWithPlaceholder
          alt={featuredImage.altText || item.product.name}
          className={cartDrawerStyles.itemImage}
          size="sm"
          src={featuredImage.url}
        />
      ) : (
        <Flex
          align="center"
          className={cartDrawerStyles.imagePlaceholder}
          justify="center"
        >
          <Text color="gray" fontSize="xs">
            No Image
          </Text>
        </Flex>
      )}

      <Flex dir="column" gap="xs" grow={1} justify="space-between">
        <Flex dir="row" justify="space-between" w="full">
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold">
              {item.product.name}
            </Text>

            {item.product.variants.length > 1 && (
              <Text color="gray" fontSize="xs">
                Variant {item.product.variants.indexOf(item.variant) + 1}
              </Text>
            )}
          </Flex>

          <Text fontSize="sm" fontWeight="semibold">
            ${totalPrice.toFixed(2)}
          </Text>
        </Flex>

        {error && (
          <Flex
            align="center"
            className={cartDrawerStyles.itemError}
            gap="xs"
            justify="space-between"
          >
            <Text color="error" fontSize="xs">
              {error}
            </Text>
            <RedoButton
              hierarchy="tertiary"
              onClick={onClearError}
              size="xs"
              text="✕"
            />
          </Flex>
        )}

        <Flex align="center" dir="row" justify="space-between" w="full">
          <Flex position="relative">
            <RedoIncrementDecrement
              disabled={isUpdating}
              max={maxQuantity}
              min={1}
              readonly={isUpdating}
              setValue={handleSetValue}
              size="small"
              value={item.quantity}
            />
            {isUpdating && (
              <Flex
                align="center"
                justify="center"
                position="absolute"
                style={{
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: "4px",
                  zIndex: 10,
                }}
              >
                <RedoSpinner size="xs" style="track" />
              </Flex>
            )}
          </Flex>

          <Text
            fontSize="sm"
            onClick={handleRemove}
            style={{ cursor: "pointer", textDecoration: "underline" }}
            textColor="tertiary"
          >
            Remove
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});

export const CartDrawer = memo(function CartDrawer({
  isOpen,
  isLoading = false,
  onClose,
  onCheckout,
}: CartDrawerProps) {
  const {
    items,
    totalPrice,
    updateQuantity,
    removeItem,
    isItemUpdating,
    getItemError,
    clearItemError,
  } = useCart();

  const handleUpdateQuantity = useCallback(
    async (productId: string, variantId: string, quantity: number) => {
      if (quantity === 0) return;
      await updateQuantity(productId, variantId, quantity);
    },
    [updateQuantity],
  );

  const handleRemoveItem = useCallback(
    async (productId: string, variantId: string) => {
      await removeItem(productId, variantId);
    },
    [removeItem],
  );

  const handleCheckout = useCallback(() => {
    onCheckout?.();
    onClose();
  }, [onCheckout, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <RedoDrawer onModalCloseRequested={onClose}>
      <Flex className={cartDrawerStyles.drawerContent} dir="column">
        <Flex align="center" justify="space-between" pt="xl" px="3xl">
          <Text fontSize="2xl" fontWeight="bold">
            Cart
          </Text>
          <RedoButton onClick={onClose} size="md" text="✕" />
        </Flex>

        <Flex className={cartDrawerStyles.itemsContainer} dir="column">
          {isLoading ? (
            <Flex
              align="center"
              className={cartDrawerStyles.emptyCart}
              justify="center"
              p="xl"
            >
              <Flex align="center" dir="column" gap="md">
                <LoadingRedoAnimation />
                <Text color="gray" fontSize="md">
                  Loading cart...
                </Text>
              </Flex>
            </Flex>
          ) : items.length === 0 ? (
            <Flex
              align="center"
              className={cartDrawerStyles.emptyCart}
              justify="center"
              p="xl"
            >
              <Text color="gray" fontSize="md">
                Your cart is empty
              </Text>
            </Flex>
          ) : (
            <>
              <Flex dir="column" gap="md">
                {items.map((item) => (
                  <CartItemRow
                    error={getItemError(item.productId, item.variantId)}
                    isUpdating={isItemUpdating(item.productId, item.variantId)}
                    item={item}
                    key={item.variantId}
                    onClearError={() =>
                      clearItemError(item.productId, item.variantId)
                    }
                    onRemove={handleRemoveItem}
                    onUpdateQuantity={handleUpdateQuantity}
                  />
                ))}
              </Flex>

              <Flex
                borderTopWidth="1px"
                dir="column"
                gap="md"
                mt="auto"
                p="3xl"
                pb="4xl"
              >
                <Flex align="center" justify="space-between">
                  <Text fontSize="xl" fontWeight="semibold">
                    Subtotal
                  </Text>
                  <Text fontSize="xl" fontWeight="bold">
                    ${totalPrice.toFixed(2)}
                  </Text>
                </Flex>

                <Text fontSize="sm" textColor="tertiary">
                  Tax included. Shipping calculated at checkout.
                </Text>

                <RedoButton
                  disabled={items.length === 0}
                  hierarchy="primary"
                  onClick={handleCheckout}
                  size="lg"
                  text="Checkout"
                />
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </RedoDrawer>
  );
});
